import { Dialog as D, DropdownMenu as M } from 'radix-ui';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { cn } from './cn';
import { Button } from './primitives';

/* ---------- dialog (shadcn pattern on Radix, prototype look) ---------- */

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="dlg-overlay" />
        <D.Content className="dlg" aria-describedby={description ? undefined : undefined}>
          <div className="dh">
            <div>
              <D.Title asChild>
                <h2>{title}</h2>
              </D.Title>
              {description ? (
                <D.Description asChild>
                  <p>{description}</p>
                </D.Description>
              ) : (
                <D.Description className="sr-only">
                  {typeof title === 'string' ? title : 'Dialog'}
                </D.Description>
              )}
            </div>
            <D.Close className="x" aria-label="Close">
              ×
            </D.Close>
          </div>
          {children && <div className="db">{children}</div>}
          {footer && <div className="df">{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/* ---------- drawer: a panel that slides in from the right edge ---------- */

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="drawer-overlay" />
        <D.Content className="drawer">
          <div className="drh">
            <div>
              <D.Title asChild>
                <h2>{title}</h2>
              </D.Title>
              {description ? (
                <D.Description asChild>
                  <p>{description}</p>
                </D.Description>
              ) : (
                <D.Description className="sr-only">
                  {typeof title === 'string' ? title : 'Panel'}
                </D.Description>
              )}
            </div>
            <D.Close className="x" aria-label="Close">
              ×
            </D.Close>
          </div>
          <div className="drb">{children}</div>
          {footer && <div className="drf">{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

/**
 * Confirmation for destructive or irreversible actions (CON-17): the consequence in
 * plain words, including whether it can be undone.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  consequence,
  confirmLabel,
  danger,
  busy,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  consequence: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  children?: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={
        <>
          <Button variant="sec" onClick={() => onOpenChange(false)}>
            Keep as is
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} busy={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="consequence">{consequence}</div>
      {children}
    </Dialog>
  );
}

/* ---------- dropdown menu (the case screen's "More") ---------- */

export interface MenuAction {
  label: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  hint?: ReactNode;
  separatorBefore?: boolean;
}

export function Menu({ trigger, actions }: { trigger: ReactNode; actions: MenuAction[] }) {
  return (
    <M.Root>
      <M.Trigger asChild>{trigger}</M.Trigger>
      <M.Portal>
        <M.Content className="dd" align="end" sideOffset={6}>
          {actions.map((a, i) => (
            <span key={typeof a.label === 'string' ? a.label : i} style={{ display: 'contents' }}>
              {a.separatorBefore && <M.Separator className="sep" />}
              <M.Item className={cn(a.danger && 'dang')} disabled={a.disabled} onSelect={a.onSelect}>
                {a.label}
                {a.hint && <span className="hint">{a.hint}</span>}
              </M.Item>
            </span>
          ))}
        </M.Content>
      </M.Portal>
    </M.Root>
  );
}

/* ---------- toast ---------- */

interface ToastMsg {
  id: number;
  text: string;
  tone?: 'bad';
}
const ToastContext = createContext<(text: string, tone?: 'bad') => void>(() => undefined);
export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<ToastMsg | null>(null);
  const seq = useRef(0);
  const show = useCallback((text: string, tone?: 'bad') => {
    seq.current += 1;
    setMsg({ id: seq.current, text, tone });
  }, []);
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(null), 4200);
    return () => clearTimeout(t);
  }, [msg]);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div aria-live="polite" role="status">
        {msg && (
          <div key={msg.id} className={cn('toast', msg.tone)}>
            {msg.text}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}
